import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { alpha } from "@mui/material/styles";
import { keyframes } from "@emotion/react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

const CONFIRM_WORD = "ELIMINAR";
const ACCENT = "#b91c1c";

const motionSafe = {
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const rise = keyframes`
  from { opacity: 0; transform: translateY(10px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const shakeBin = keyframes`
  0%, 100% { transform: rotate(0deg) translateY(0); }
  25% { transform: rotate(-8deg) translateY(-2px); }
  50% { transform: rotate(6deg) translateY(0); }
  75% { transform: rotate(-4deg) translateY(-1px); }
`;

const fadeAway = keyframes`
  0%, 20% { opacity: 1; transform: translateY(0) scale(1); }
  55% { opacity: 0.55; transform: translateY(10px) scale(0.92); }
  100% { opacity: 0.15; transform: translateY(28px) scale(0.78); }
`;

const bob = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
`;

function DeleteYearPreviewStage({ year }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: 88,
        mt: 1.35,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: alpha(ACCENT, 0.08),
        border: "1px solid",
        borderColor: alpha(ACCENT, 0.22),
        animation: `${rise} 320ms ease both`,
        ...motionSafe,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: "20%",
          top: 12,
          width: 54,
          height: 60,
          borderRadius: 1.5,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(ACCENT, 0.35),
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.2,
          boxShadow: `0 6px 14px ${alpha(ACCENT, 0.14)}`,
          animation: `${fadeAway} 2.8s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <CalendarMonthOutlinedIcon sx={{ fontSize: 22, color: ACCENT }} />
        <Typography
          sx={{ fontSize: "0.62rem", fontWeight: 900, color: ACCENT }}
        >
          {year}
        </Typography>
      </Box>
      <Box
        sx={{
          position: "absolute",
          right: "18%",
          top: 18,
          width: 52,
          height: 52,
          borderRadius: 1.5,
          bgcolor: alpha(ACCENT, 0.14),
          border: "1.5px solid",
          borderColor: alpha(ACCENT, 0.45),
          display: "grid",
          placeItems: "center",
          boxShadow: `0 8px 18px ${alpha(ACCENT, 0.2)}`,
          animation: `${shakeBin} 2.4s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <DeleteOutlineOutlinedIcon sx={{ fontSize: 28, color: ACCENT }} />
      </Box>
      <Box
        sx={{
          position: "absolute",
          left: 12,
          bottom: 10,
          px: 0.85,
          py: 0.35,
          borderRadius: 1,
          bgcolor: alpha(ACCENT, 0.12),
          border: "1px solid",
          borderColor: alpha(ACCENT, 0.35),
          animation: `${bob} 2s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <Typography
          sx={{ fontSize: "0.58rem", fontWeight: 900, color: ACCENT }}
        >
          {year}
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * Borrar año completo: paso 1 aviso → paso 2 escribir año + ELIMINAR.
 */
export default function DesignCalendarDeleteYearDialog({
  open,
  year,
  notasCount = 0,
  accionesCount = 0,
  deleting = false,
  error = "",
  onClose,
  onConfirm,
}) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [confirmAnio, setConfirmAnio] = useState("");
  const [confirmWord, setConfirmWord] = useState("");

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setConfirmAnio("");
    setConfirmWord("");
  }, [open, year]);

  const canDelete =
    !deleting &&
    Number(confirmAnio) === Number(year) &&
    String(confirmWord).trim().toUpperCase() === CONFIRM_WORD;

  return (
    <Dialog
      open={open}
      onClose={deleting ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      keepMounted={false}
      transitionDuration={160}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            backgroundColor: "rgba(15, 23, 42, 0.45)",
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          animation: open ? `${rise} 320ms ease both` : undefined,
          ...motionSafe,
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          background: `linear-gradient(135deg, ${alpha(ACCENT, 0.14)} 0%, ${alpha(
            "#ea580c",
            0.08,
          )} 100%)`,
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(ACCENT, 0.16),
                color: ACCENT,
                flexShrink: 0,
                animation: `${shakeBin} 2.4s ease-in-out infinite`,
                ...motionSafe,
              }}
            >
              <DeleteOutlineOutlinedIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 800, lineHeight: 1.15 }}
              >
                {t(
                  "designCalendar.deleteYearTitle",
                  "Eliminar calendario {{year}}",
                  {
                    year,
                  },
                )}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", lineHeight: 1.4, mt: 0.3 }}
              >
                {step === 1
                  ? t(
                      "designCalendar.deleteYearHint1",
                      "Se borran todas las notas y arranques de este año. No se puede deshacer.",
                    )
                  : t(
                      "designCalendar.deleteYearHint2",
                      "Confirmación final: escribe el año y la palabra ELIMINAR.",
                    )}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            aria-label={t("designCalendar.close", "Cerrar")}
            onClick={onClose}
            disabled={deleting}
            size="small"
          >
            <CloseOutlinedIcon />
          </IconButton>
        </Stack>
        <DeleteYearPreviewStage year={year} />
      </Box>

      <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
        {step === 1 ? (
          <Stack
            spacing={1.25}
            sx={{
              animation: `${rise} 300ms ease both`,
              ...motionSafe,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <WarningAmberRoundedIcon
                sx={{ color: ACCENT, fontSize: 22, mt: 0.15 }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ lineHeight: 1.5, fontWeight: 600 }}
              >
                {t(
                  "designCalendar.deleteYearBody",
                  "Vas a borrar {{notas}} notas y {{acciones}} arranques del año {{year}}.",
                  {
                    year,
                    notas: notasCount,
                    acciones: accionesCount,
                  },
                )}
              </Typography>
            </Stack>
          </Stack>
        ) : (
          <Stack
            spacing={1.25}
            sx={{
              animation: `${rise} 300ms ease both`,
              ...motionSafe,
            }}
          >
            <TextField
              autoFocus
              size="small"
              fullWidth
              label={t(
                "designCalendar.deleteYearTypeYear",
                "Escribe el año {{year}}",
                { year },
              )}
              value={confirmAnio}
              onChange={(event) => setConfirmAnio(event.target.value)}
              disabled={deleting}
              inputProps={{ inputMode: "numeric", autoComplete: "off" }}
            />
            <TextField
              size="small"
              fullWidth
              label={t(
                "designCalendar.deleteYearTypeWord",
                "Escribe {{word}}",
                { word: CONFIRM_WORD },
              )}
              value={confirmWord}
              onChange={(event) => setConfirmWord(event.target.value)}
              disabled={deleting}
              placeholder={CONFIRM_WORD}
              inputProps={{ autoComplete: "off" }}
            />
          </Stack>
        )}
        {error ? (
          <Typography sx={{ mt: 1.25, fontSize: "0.78rem", color: ACCENT }}>
            {error}
          </Typography>
        ) : null}
      </Box>

      <Stack
        direction="row"
        spacing={1.25}
        justifyContent="flex-end"
        sx={{
          px: 2.5,
          py: 1.75,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: (th) => alpha(th.palette.background.default, 0.65),
        }}
      >
        <Button
          onClick={onClose}
          disabled={deleting}
          sx={{ fontWeight: 700, minHeight: 44, textTransform: "none" }}
        >
          {t("designCalendar.close", "Cerrar")}
        </Button>
        {step === 1 ? (
          <Button
            variant="contained"
            color="error"
            disabled={deleting || (notasCount === 0 && accionesCount === 0)}
            onClick={() => setStep(2)}
            sx={{
              minHeight: 48,
              fontWeight: 800,
              borderRadius: 2,
              textTransform: "none",
              boxShadow: `0 8px 18px ${alpha(ACCENT, 0.28)}`,
            }}
          >
            {t("designCalendar.deleteYearContinue", "Continuar")}
          </Button>
        ) : (
          <Button
            variant="contained"
            color="error"
            disabled={!canDelete}
            onClick={() =>
              onConfirm?.({
                anio: Number(year),
                confirm: CONFIRM_WORD,
                confirmAnio: Number(confirmAnio),
              })
            }
            startIcon={
              deleting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteOutlineOutlinedIcon />
              )
            }
            sx={{
              minHeight: 48,
              fontWeight: 800,
              borderRadius: 2,
              textTransform: "none",
              boxShadow: `0 8px 18px ${alpha(ACCENT, 0.28)}`,
            }}
          >
            {t("designCalendar.deleteYearConfirm", "Eliminar año")}
          </Button>
        )}
      </Stack>
    </Dialog>
  );
}
